import { z } from 'zod';

import type {
  CreateCashTreasuryFundingRequest,
  CreateCashTreasuryFundingResponse,
} from './cash-treasury-funding.dto.js';
import { CASH_DENOMINATION_ALLOCATION_TOTAL_MISMATCH } from './cash-error-codes.js';
import { CashDenominationRowsSchema } from './cash-denominations.schema.js';
import { CashFundingSourceTypeSchema, CashMovementDtoSchema } from './cash-movement.schema.js';
import { CashPositionItemDtoSchema } from './cash-position.schema.js';
import type { CashMovementDto } from './cash-movement.dto.js';
import type { CashPositionItemDto } from './cash-position.dto.js';

const timestampSchema = z.iso.datetime();

const TreasuryFundingMovementDtoSchema: z.ZodType<CashMovementDto> =
  CashMovementDtoSchema.superRefine((value, ctx) => {
    if (value.movementKind !== 'funding') {
      ctx.addIssue({
        code: 'custom',
        message: 'cashMovement.movementKind must be "funding"',
        path: ['movementKind'],
      });
    }

    if (value.direction !== 'in') {
      ctx.addIssue({
        code: 'custom',
        message: 'cashMovement.direction must be "in"',
        path: ['direction'],
      });
    }

    if (value.storeId !== null || value.locationId !== null || value.stationId !== null) {
      ctx.addIssue({
        code: 'custom',
        message: 'Treasury funding movements must be company-scoped',
        path: ['cashLocationId'],
      });
    }

    if (value.cashSessionId !== null || value.transferId !== null || value.expenseId !== null) {
      ctx.addIssue({
        code: 'custom',
        message: 'Treasury funding movements must not target sessions, transfers, or expenses',
        path: ['cashSessionId'],
      });
    }

    if (!value.fundingSourceType) {
      ctx.addIssue({
        code: 'custom',
        message: 'cashMovement.fundingSourceType is required',
        path: ['fundingSourceType'],
      });
    }
  });

const TreasuryFundingTargetBalanceDtoSchema: z.ZodType<CashPositionItemDto> =
  CashPositionItemDtoSchema.superRefine((value, ctx) => {
    if (value.kind !== 'treasury') {
      ctx.addIssue({
        code: 'custom',
        message: 'targetBalance.kind must be "treasury"',
        path: ['kind'],
      });
    }

    if (value.scope !== 'company') {
      ctx.addIssue({
        code: 'custom',
        message: 'targetBalance.scope must be "company"',
        path: ['scope'],
      });
    }
  });

export const CreateCashTreasuryFundingRequestSchema: z.ZodType<CreateCashTreasuryFundingRequest> = z
  .object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    treasuryCashLocationId: z.uuid(),
    amount: z.number().int().positive(),
    sourceType: CashFundingSourceTypeSchema,
    fundingSourceCategoryId: z.uuid().nullable().optional(),
    reference: z.string().trim().min(1).max(120).nullable().optional(),
    note: z.string().trim().min(1).max(500).nullable().optional(),
    occurredAt: timestampSchema.optional(),
    denominations: CashDenominationRowsSchema.nullable().optional(),
  })
  .superRefine((value, context) => {
    if (value.sourceType === 'other' && !value.note?.trim()) {
      context.addIssue({
        code: 'custom',
        message: 'Note is required when Treasury funding source is Other.',
        path: ['note'],
      });
    }

    if (!value.denominations) {
      return;
    }

    const denominationTotalMinor = value.denominations.reduce(
      (total, row) => total + row.denom * row.count,
      0,
    );
    if (denominationTotalMinor !== value.amount) {
      context.addIssue({
        code: 'custom',
        message: 'Denomination total does not match the Treasury funding amount.',
        path: ['denominations'],
        params: { apiErrorCode: CASH_DENOMINATION_ALLOCATION_TOTAL_MISMATCH },
      });
    }
  });

export const CreateCashTreasuryFundingResponseSchema: z.ZodType<CreateCashTreasuryFundingResponse> =
  z.object({
    cashMovement: TreasuryFundingMovementDtoSchema,
    targetBalance: TreasuryFundingTargetBalanceDtoSchema,
  });
